/**
 * OperationHistoryEventTypesFilter Component
 *
 * Generic CollapsingMultiSelect filter for operation history event types.
 * Accepts a getLabel callback to resolve event type display names.
 */

import { memo, useMemo } from 'react';

import { IconCategory } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { CollapsingMultiSelect } from '@/components/ui/CollapsingMultiSelect';
import { ICON_SIZE_SM } from '@/constants/ui';
import { Z_INDEX_MOBILE_MODAL_DATEPICKER } from '@/constants/zIndex';

import styles from './OperationHistoryEventTypesFilter.module.css';

interface OperationHistoryEventTypesFilterProps {
  selectedTypes: string[];
  availableTypes: string[];
  getLabel: (eventType: string) => string;
  onChange: (types: string[]) => void;
}

export const OperationHistoryEventTypesFilter = memo(
  function OperationHistoryEventTypesFilter({
    selectedTypes,
    availableTypes,
    getLabel,
    onChange,
  }: OperationHistoryEventTypesFilterProps) {
    const { t } = useTranslation();

    const eventTypeOptions = useMemo(() => {
      if (!availableTypes || availableTypes.length === 0) {
        return [];
      }

      return availableTypes
        .filter((eventType) => eventType != null)
        .map((eventType) => ({
          value: eventType,
          label: getLabel(eventType),
        }));
    }, [availableTypes, getLabel]);

    return (
      <CollapsingMultiSelect
        label={t('history.eventTypes')}
        placeholder={t('history.eventTypesPlaceholder')}
        data={eventTypeOptions}
        value={selectedTypes}
        onChange={onChange}
        leftSection={<IconCategory size={ICON_SIZE_SM} />}
        searchable
        clearable
        className={styles.multiSelect}
        comboboxProps={{
          zIndex: Z_INDEX_MOBILE_MODAL_DATEPICKER,
          withinPortal: true,
        }}
      />
    );
  }
);
